import React from 'react';
import cn from 'classnames';

import GRAPHICS_TAB_TEAM from 'assets/slickpay-splash/time-off/graphics-tab-one.png';
import GRAPHICS_TAB_PAY from 'assets/slickpay-splash/time-off/graphics-tab-three.svg';
import GRAPHICS_TAB_HR from 'assets/slickpay-splash/time-off/graphics-tab-two.svg';
import ICON_TAB_HR from 'assets/slickpay-splash/time-off/hr-ico.svg';
import ICON_TAB_PAY from 'assets/slickpay-splash/time-off/pay-ico.svg';
import ICON_TAB_TEAM from 'assets/slickpay-splash/time-off/team-ico.svg';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import createNodeKey from 'utils/react/createNodeKey.util';
import C from 'utils/values/constant.util';

import CN from './TimeOffSplashTabs.module.scss';

const TimeOffSplashTeamTab = ({ title }) => (
  <div className={CN.tabPanel} data-bem="TimeOffSplashTeamTab">
    <div className={CN.text}>
      <div className={CN.title}>{title}</div>
      <div className={CN.block}>
        <div className={CN.blockTitle}>Staff Targets & Reports</div>
        <div className={CN.blockText}>
          Set custom targets per staff member and allow employed and self-employed staff to view their progress.
        </div>
      </div>
      <div className={CN.block}>
        <div className={CN.blockTitle}>Team Incentives</div>
        <div className={CN.blockText}>
          Set monthly incentives & targets for your team. They earn more. You earn more. Let Slick motivate and inspire
          your team.
        </div>
      </div>
    </div>
    <div className={CN.graphics}>
      <img src={GRAPHICS_TAB_TEAM} alt={title} className={CN.illustration} />
    </div>
  </div>
);

const TimeOffSplashManagementTab = ({ title }) => (
  <div className={CN.tabPanel} data-bem="TimeOffSplashManagementTab">
    <div className={CN.text}>
      <div className={CN.title}>{title}</div>
      <div className={CN.block}>
        <div className={CN.blockTitle}>Time Off Tracking</div>
        <div className={CN.blockText}>
          Take control of your team and log staff holiday requests and set allowances per staff member.
        </div>
      </div>
      <div className={CN.block}>
        <div className={CN.blockTitle}>Absence Management</div>
        <div className={CN.blockText}>
          Create custom categories to suit your business such as sick leave, parental leave, professional development.
        </div>
      </div>
    </div>
    <div className={CN.graphics}>
      <img src={GRAPHICS_TAB_HR} alt={title} className={CN.illustration} />
    </div>
  </div>
);

const TimeOffSplashPayrollTab = ({ title }) => (
  <div className={CN.tabPanel} data-bem="TimeOffSplashPayrollTab">
    <div className={CN.text}>
      <div className={CN.title}>{title}</div>
      <div className={CN.block}>
        <div className={CN.blockTitle}>Commissions</div>
        <div className={CN.blockText}>
          Calculate commissions at the click of a button. You choose a structure that works for you and Slick will build
          it.
        </div>
      </div>
      <div className={CN.block}>
        <div className={CN.blockTitle}>Days worked</div>
        <div className={CN.blockText}>
          Set & automated self-employed rates per chair. Calculate weekly/monthly wages or give access to your
          accountant and let them do the work.
        </div>
      </div>
    </div>
    <div className={CN.graphics}>
      <img src={GRAPHICS_TAB_PAY} alt={title} className={CN.illustration} />
    </div>
  </div>
);

const TAB = Object.freeze({
  team: 'team',
  hr: 'hr',
  pay: 'payment',
});

const COMPONENT = Object.freeze({
  [TAB.team]: <TimeOffSplashTeamTab title="Team Management" />,
  [TAB.hr]: <TimeOffSplashManagementTab title="HR Management" />,
  [TAB.pay]: <TimeOffSplashPayrollTab title="Payroll" />,
});

const INFO = C({
  [TAB.team]: {
    heading: 'Team Management',
    headingMobile: 'Team',
    icon: ICON_TAB_TEAM,
  },
  [TAB.hr]: {
    heading: 'HR Management',
    headingMobile: 'HR',
    icon: ICON_TAB_HR,
  },
  [TAB.pay]: {
    heading: 'Payroll',
    headingMobile: 'Payroll',
    icon: ICON_TAB_PAY,
  },
});

const TimeOffSplashTabs = () => {
  const [selected, setSelected] = useStateWhileMounted(TAB.team);

  return (
    <div className={CN.container} data-bem="TimeOffSplashTabs">
      <div className={CN.tabList} data-bem="TimeOffSplashTabs__tabList">
        {Object.entries(INFO).map(([tag, { heading, headingMobile, icon }], index) => (
          <div
            data-bem="TimeOffSplashTabs__tab"
            role="button"
            tabIndex={0}
            key={createNodeKey({ index, tag })}
            className={cn({
              [CN.tab]: true,
              [CN.activeTab]: selected === tag,
            })}
            onClick={() => void setSelected(tag)}
          >
            <img src={icon} alt={heading} data-bem="TimeOffSplashTabs__image" />
            <div className={CN.tabTitle} data-bem="TimeOffSplashTabs__tabTitle">
              <span className={CN.normalTitle}>{heading}</span>
              <span className={CN.mobileTitle}>{headingMobile}</span>
            </div>
          </div>
        ))}
      </div>

      <div className={CN.tabsPanel} data-bem="TimeOffSplashTabs__tabsPanel">
        {COMPONENT[selected]}
      </div>
    </div>
  );
};

export default TimeOffSplashTabs;
